import React, { useEffect, useState, useRef } from 'react';
import styled from 'styled-components/macro';
import HtmlParser from 'html-react-parser';
import DOMPurify from 'dompurify';

import { fontDefault, fontkoKR, CloseButton, IconVolumeOn } from '@mirinae/react-ui';
import { os } from '@mirinae/js-utils/utils/userAgent';

import Element from '@mirinae/lessons/components/lesson/elements/Element';
import {
    FormattedKoPhrase,
    FormattedKoEnPhrase,
    FormattedPhrase
} from '@mirinae/lessons/components/lesson/elements/Phrase';
import { HelpBubble, registerHelp } from '@mirinae/lessons/components/lesson/Help';
import { LessonButton } from '@mirinae/lessons/modules/defines/styles';
import IconZoom from '@mirinae/lessons/components/lesson/icons/icon-zoom.svg';
import { playTTS } from '@mirinae/lessons/modules/libs/lessonLib';
import { zIndex } from '@mirinae/lessons/modules/defines/styles';
import ManHover from '@mirinae/lessons/assets/images/man-hovered.svg';
import ManPlaying from '@mirinae/lessons/assets/images/man-playing.svg';
import ManReady from '@mirinae/lessons/assets/images/man-ready.svg';
import WomanHover from '@mirinae/lessons/assets/images/woman-hovered.svg';
import WomanPlaying from '@mirinae/lessons/assets/images/woman-playing.svg';
import WomanReady from '@mirinae/lessons/assets/images/woman-ready.svg';

import axios from 'axios';

const ExampleKoEnStyle = { align: 'left', koFontWeight: 400, koreanFontSize: '20px', englishFontSize: '13px', enFontWeight: 500 };

const VariantTableElement = styled(Element)`
    position: relative;
    font-family: ${fontDefault};
    ${!os.isOthers && `
        margin-bottom: 24px;
        width: calc(100vw - 60px);
    `}
`;

const VariantTable = styled.div`
    position: relative;
    border: 1px solid #DFDFDF;
    border-radius: 10px;
    text-align: center;
    max-width: 100%;
`;

const VariantTableMobile = styled(VariantTable)`
    width: 100%;  
`;

const Header = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    width: 100%;
    //display: grid;
    //grid-template-columns: 135px 572px;
    border-bottom: 1px solid #DFDFDF;
    height: 45px;
    overflow: hidden;
`;

const HeaderMobile = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: start;
    width: 100%;
`;

const HeaderBlock = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: start;
    height: 100%;
    min-width: 0;
`;

const HeaderBlockMobile = styled(HeaderBlock)`
    height: 40px;
    border-bottom: 1px solid #DFDFDF;
`;

const Variant = styled.div`
    display: flex;
    align-items: center;
    position: relative;
    font-family: ${fontkoKR};
    font-weight: 700;
    color: #F6C844;
    padding: 0 14px;
    text-align: left;
    min-width: fit-content;
`;

const Condition = styled.div`
    display: inline;
    align-self: center;
    color: var(--primary-3-c-3-c-3-b-black,#3C3C3B);
    font-size: 14px;
    font-weight: 400;
    line-height: 110%;
    padding: 0px 14px;
    text-align: left;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    flex-grow: 1;
    // flex-shrink: 1; /* Allows the item to shrink if necessary */
    // flex-basis: 0;  /* Start with a basis of 0 */
    min-width: 0;
    width: auto;
`;

const Examples = styled.div`
    display: flex;
    flex-direction: column;
    align-items: start;
    justify-content: center;
    height: 255px;
    overflow-y: auto;
`;


const MobileExamples = styled.div`
    display: flex;
    flex-direction: column;
    align-items: start;
`;

const ExampleWrapper = styled.div`
    position: relative;
    display: flex;
    flex-direction: column;
    width: 100%;
    gap: 15px;
    padding: 15px;
    & + & {
        border-bottom: 1px solid #DFDFDF;        
    }
`;

const ExampleRow = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    width: 100%;
    gap: 10px;
`;

const ExampleColumn = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 100%;
    gap: 10px;
`;

const ExampleText = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
    gap: 10px;
`;

const ExamplePhrase = styled.div`
    display: flex;
    flex-direction: column;
    align-items: start;
    justify-content: center;
    margin-left: 13px;
`;

const Label = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    color: #7A7A7A;
    font-weight: 700;
    font-size: 12px;
    background-color: #EFF4F7;
    border-bottom: 1px solid #fff;
    padding: 0 14px;
    min-width: 100px;
`;

const Cell = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    padding: 0 25px;
    font-weight: 400;
    font-size: 14px;
    border-bottom: 1px solid #DFDFDF;
    border-left: 1px solid #DFDFDF;
`;

const VariantAnnotation = styled.div`
    font-weight: 700;
    font-size: 15px;
     ${os.isOthers ? 'margin-top: -5px' : 'margin-left: 5px'};
`;

const ExamplePart = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: start;
`;

const TableMobile = styled.div`
    border: 1px solid #DFDFDF;
    border-radius: 10px;
    overflow: hidden;
    & + & {
        margin-top: 10px;
    }
`;

const EmbeddedSVG = styled.div`
    width: 160px;
    height: 135px;
    border-radius: 10px;
`;

const EmbeddedImg = styled.img`
    width: 160px;
    height: 135px;
    border-radius: 10px;
`;

const Graphic = ({ graphic, scale }) => {
    const [cleanSVG, setCleanSVG] = useState('');
    const gotSVG = useRef(false);
    const svgEmbedRef = useRef(null);
    const scaleVal = scale ? scale.value : 1.0;

    useEffect(() => {
        if (graphic.mimeType === 'image/svg+xml' && svgEmbedRef.current && !gotSVG.current) {
            gotSVG.current = true;
            axios(graphic.url, {
                method: 'GET',
                withCredentials: false,
                headers: { 'Content-Type': 'image/svg+xml', },
            }).then(response => {
                // console.log(response);
                setCleanSVG(DOMPurify.sanitize(response.data, { ADD_TAGS: ['script'] }));
            })
            .catch(error => {
                console.log('error');
            });
        }
    }, []);

    // it appears the script contained in the retrieved SVG is not automatically running, pull it out here and run it by hand.
    useEffect(() => {
        const parser = new DOMParser();
        const doc = parser.parseFromString(cleanSVG, 'image/svg+xml');
        const scriptElement = doc.querySelector('script');

        if (scriptElement) {
            const script = document.createElement('script');
            script.innerHTML = scriptElement.textContent;
            document.body.appendChild(script);
            return () => {
                document.body.removeChild(script);
            };
        }
    }, [cleanSVG]);

    return (
        graphic.mimeType === 'image/svg+xml' ? (
            <EmbeddedSVG ref={svgEmbedRef} dangerouslySetInnerHTML={{__html: cleanSVG}} style={{ transform: `scale(${scaleVal})` }} />
        ) : (
            <EmbeddedImg src={graphic.url} style={{ transform: `scale(${scaleVal})` }} />
        )
    );
};

const AdvancedVariantTable = ({ element, variant, example, translation }) => {
    const [audioEl, setAudioEl] = useState(null);
    const [playIndex, setPlayIndex] = useState(-1);
    const [iconHover, setIconHover] = useState([]);

    const iconsF = {
        'Playing': () => <img src={WomanPlaying} alt="Woman playing" />,
        'Ready': () => <img src={WomanReady} alt="Woman ready" />,
        'Hover': () => <img src={WomanHover} alt="Woman hover" />,
    };
    const iconsM = {
        'Playing': () => <img src={ManPlaying} alt="Man playing" />,
        'Ready': () => <img src={ManReady} alt="Man ready" />,
        'Hover': () => <img src={ManHover} alt="Man hover" />,
    };

    const koreanEmphasisConverter = (sentence) => {
        const re = /[\u3131-\uD79D]/ugi;
        const s = sentence.split(' ');
        const result = s.map((d) => (d.match(re) ? `<strong>${d}</strong>` : d));
        return HtmlParser(result.join(' '));
    };

    const handleTTS = (index, s, options) => {
        setPlayIndex(index);
        const sentence = s.reduce((prev, curr) => (`${prev.value || prev}${curr.value}`), '');
        playTTS(audioEl, sentence, null, options);
    };

    const setPlayHover = (index, value) => {
        iconHover[index] = value;
        setIconHover([...iconHover]);
    }

    const onePart = (f) => f.hiddenParts && f.hiddenParts.includes('koreanParts2');

    return (
        <VariantTableElement element={element} className="variant-table">
            {os.isOthers ? (
                <VariantTable element={element}>
                    <Header>
                        { !element.fieldsByName.variantBox.hidden && (
                            <HeaderBlock style={{ minWidth: 'fit-content' }}>
                                <Label>{element.fieldsByName.variantBox.parts.englishParts[0].value}</Label>
                                <Variant>
                                    {variant.fieldsByName.variant.value}
                                    {variant.fieldsByName.annotation.value && <VariantAnnotation>{v.fieldsByName.annotation.value}</VariantAnnotation>}
                                </Variant>
                            </HeaderBlock>
                        )}
                        { !element.fieldsByName.conditionBox.hidden && (
                            <HeaderBlock>
                                <Label>{element.fieldsByName.conditionBox.parts.englishParts[0].value}</Label>
                                <Condition>
                                    {koreanEmphasisConverter(variant.fieldsByName.condition.value)}
                                </Condition>
                            </HeaderBlock>
                        )}
                    </Header>

                    <Examples>
                        <ExampleWrapper key={example.index}>
                            <ExampleRow>
                                { example.parts.graphic[0]?.url && <Graphic graphic={example.parts.graphic[0]} /> }
                                <ExampleText>
                                    { onePart(example) ? (
                                        <ExamplePart>
                                            <div
                                                style={{ cursor: 'pointer' }}
                                                onClick={() => handleTTS(example.index, example.parts.koreanParts1, { speed: '100%', speaker: 'female' })}
                                                onMouseOver={() => !iconHover[example.index] && setPlayHover(example.index, true)}
                                                onMouseLeave={() => iconHover[example.index] && setPlayHover(example.index, false)}
                                            >
                                                {playIndex === example.index ? <iconsF.Playing /> : playIndex === -1 ? iconHover[example.index] ? <iconsF.Hover /> : <iconsF.Ready /> : <iconsF.Ready />}
                                            </div>
                                            <ExamplePhrase>
                                                <FormattedPhrase parts={example.parts.koreanParts1} phraseStyle={ExampleKoEnStyle} language="ko" />
                                                { translation && <FormattedPhrase parts={example.parts.englishParts1} phraseStyle={ExampleKoEnStyle} language="en" /> }
                                            </ExamplePhrase>
                                        </ExamplePart>
                                    ) : (
                                        <>
                                            <ExamplePart>
                                                <div
                                                    style={{ cursor: 'pointer' }}
                                                    onClick={() => handleTTS(example.index, example.parts.koreanParts1, { speed: '100%', speaker: 'male' })}
                                                    onMouseOver={() => !iconHover[example.index] && setPlayHover(example.index, true)}
                                                    onMouseLeave={() => iconHover[example.index] && setPlayHover(example.index, false)}
                                                >
                                                    {playIndex === example.index ? <iconsM.Playing /> : playIndex === -1 ? iconHover[example.index] ? <iconsM.Hover /> : <iconsM.Ready /> : <iconsM.Ready />}
                                                </div>
                                                <ExamplePhrase>
                                                    <FormattedPhrase parts={example.parts.koreanParts1} phraseStyle={ExampleKoEnStyle} language="ko" />
                                                    { translation && <FormattedPhrase parts={example.parts.englishParts1} phraseStyle={ExampleKoEnStyle} language="en" /> }
                                                </ExamplePhrase>
                                            </ExamplePart>
                                            <ExamplePart>
                                                <div
                                                    style={{ cursor: 'pointer' }}
                                                    onClick={() => handleTTS(example.index + 100, example.parts.koreanParts2, { speed: '100%', speaker: 'female' })}
                                                    onMouseOver={() => !iconHover[example.index + 100] && setPlayHover(example.index + 100, true)}
                                                    onMouseLeave={() => iconHover[example.index + 100] && setPlayHover(example.index + 100, false)}
                                                >
                                                    {playIndex === example.index + 100 ? <iconsF.Playing /> : playIndex === -1 ? iconHover[example.index + 100] ? <iconsF.Hover /> : <iconsF.Ready /> : <iconsF.Ready />}
                                                </div>
                                                <ExamplePhrase>
                                                    <FormattedPhrase parts={example.parts.koreanParts2} phraseStyle={ExampleKoEnStyle} language="ko" />
                                                    { translation && <FormattedPhrase parts={example.parts.englishParts2} phraseStyle={ExampleKoEnStyle} language="en" /> }
                                                </ExamplePhrase>
                                            </ExamplePart>
                                        </>
                                    )}
                                </ExampleText>
                            </ExampleRow>
                        </ExampleWrapper>
                    </Examples>
                </VariantTable>
            ) : (
                <VariantTableMobile>
                    <HeaderMobile>
                        { !element.fieldsByName.variantBox.hidden && (
                            <HeaderBlockMobile>
                                <Label>{element.fieldsByName.variantBox.parts.englishParts[0].value}</Label>
                                <Variant>
                                    {variant.fieldsByName.variant.value}
                                    {variant.fieldsByName.annotation.value && <VariantAnnotation>{v.fieldsByName.annotation.value}</VariantAnnotation>}
                                </Variant>
                            </HeaderBlockMobile>
                        )}
                        { !element.fieldsByName.conditionBox.hidden && (
                            <HeaderBlockMobile>
                                <Label>{element.fieldsByName.conditionBox.parts.englishParts[0].value}</Label>
                                <Condition>
                                    {koreanEmphasisConverter(variant.fieldsByName.condition.value)}
                                </Condition>
                            </HeaderBlockMobile>
                        )}
                    </HeaderMobile>

                    <MobileExamples>
                        <ExampleWrapper key={example.index}>
                            <ExampleColumn>
                                { example.parts.graphic[0]?.url && <Graphic graphic={example.parts.graphic[0]} /> }
                                <ExampleText>
                                    { onePart(example) ? (
                                        <ExamplePart>
                                            <div
                                                style={{ cursor: 'pointer' }}
                                                onClick={() => handleTTS(example.index, example.parts.koreanParts1, { speed: '100%', speaker: 'female' })}
                                                onMouseOver={() => !iconHover[example.index] && setPlayHover(example.index, true)}
                                                onMouseLeave={() => iconHover[example.index] && setPlayHover(example.index, false)}
                                            >
                                                {playIndex === example.index ? <iconsF.Playing /> : playIndex === -1 ? iconHover[example.index] ? <iconsF.Hover /> : <iconsF.Ready /> : <iconsF.Ready />}
                                            </div>
                                            <ExamplePhrase>
                                                <FormattedPhrase parts={example.parts.koreanParts1} phraseStyle={ExampleKoEnStyle} language="ko" />
                                                { translation && <FormattedPhrase parts={example.parts.englishParts1} phraseStyle={ExampleKoEnStyle} language="en" /> }
                                            </ExamplePhrase>
                                        </ExamplePart>
                                    ) : (
                                        <>
                                            <ExamplePart>
                                                <div
                                                    style={{ cursor: 'pointer' }}
                                                    onClick={() => handleTTS(example.index, example.parts.koreanParts1, { speed: '100%', speaker: 'male' })}
                                                    onMouseOver={() => !iconHover[example.index] && setPlayHover(example.index, true)}
                                                    onMouseLeave={() => iconHover[example.index] && setPlayHover(example.index, false)}
                                                >
                                                    {playIndex === example.index ? <iconsM.Playing /> : playIndex === -1 ? iconHover[example.index] ? <iconsM.Hover /> : <iconsM.Ready /> : <iconsM.Ready />}
                                                </div>
                                                <ExamplePhrase>
                                                    <FormattedPhrase parts={example.parts.koreanParts1} phraseStyle={ExampleKoEnStyle} language="ko" />
                                                    { translation && <FormattedPhrase parts={example.parts.englishParts1} phraseStyle={ExampleKoEnStyle} language="en" /> }
                                                </ExamplePhrase>
                                            </ExamplePart>
                                            <ExamplePart>
                                                <div
                                                    style={{ cursor: 'pointer' }}
                                                    onClick={() => handleTTS(example.index + 100, example.parts.koreanParts2, { speed: '100%', speaker: 'female' })}
                                                    onMouseOver={() => !iconHover[example.index + 100] && setPlayHover(example.index + 100, true)}
                                                    onMouseLeave={() => iconHover[example.index + 100] && setPlayHover(example.index + 100, false)}
                                                >
                                                    {playIndex === example.index + 100 ? <iconsF.Playing /> : playIndex === -1 ? iconHover[example.index + 100] ? <iconsF.Hover /> : <iconsF.Ready /> : <iconsF.Ready />}
                                                </div>
                                                <ExamplePhrase>
                                                    <FormattedPhrase parts={example.parts.koreanParts2} phraseStyle={ExampleKoEnStyle} language="ko" />
                                                    { translation && <FormattedPhrase parts={example.parts.englishParts2} phraseStyle={ExampleKoEnStyle} language="en" /> }
                                                </ExamplePhrase>
                                            </ExamplePart>
                                        </>
                                    )}
                                </ExampleText>
                            </ExampleColumn>
                        </ExampleWrapper>
                    </MobileExamples>
                </VariantTableMobile>
            )}
        <audio
            ref={setAudioEl}
            // onPlay={() => setIconMode('playing')}
            onPause={() => setPlayIndex(-1)}
            onEnded={() => setPlayIndex(-1)}
        />
        </VariantTableElement>
    );
};

export default AdvancedVariantTable;
